import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../styles/globals';

const TestimonialsContainer = styled.section`
  background: linear-gradient(135deg, ${colors.darkBackground} 0%, ${colors.primary} 100%);
  color: ${colors.white};
  padding: 80px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.2;
  }

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const TestimonialsContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const TestimonialsTitle = styled.h2`
  font-family: ${fonts.playfair};
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  margin-bottom: 16px;
  line-height: 1.2;
  background: linear-gradient(135deg, ${colors.white} 0%, ${colors.accent} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const TestimonialsSubtitle = styled.p`
  font-family: ${fonts.inter};
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 50px;
  line-height: 1.5;
  opacity: 0.95;
`;

const TestimonialsCarousel = styled.div`
  position: relative;
  margin-top: 40px;
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: 10px' : 'right: 10px'};
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.white};
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-50%) scale(1.1);
  }

  @media (max-width: 768px) {
    ${props => props.direction === 'left' ? 'left: 5px' : 'right: 5px'};
    width: 40px;
    height: 40px;
    font-size: 16px;
  }
`;

const TestimonialCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 40px 30px;
  margin: 0 auto;
  max-width: 600px;
  opacity: ${props => props.isActive ? 1 : 0};
  transform: ${props => props.isActive ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(20px)'};
  transition: all 0.5s ease;
  position: ${props => props.isActive ? 'relative' : 'absolute'};
  top: 0;
  left: 50%;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const TestimonialHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 25px;
  text-align: left;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
`;

const TestimonialImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.accent} 0%, ${colors.secondary} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 700;
  color: ${colors.white};
  box-shadow: 0 8px 20px rgba(243, 156, 107, 0.3);
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 24px;
  }
`;

const TestimonialInfo = styled.div`
  flex: 1;
`;

const TestimonialAuthor = styled.div`
  font-family: ${fonts.inter};
  font-size: 1.1rem;
  font-weight: 600;
  color: ${colors.accent};
  margin-bottom: 5px;
`;

const TestimonialRole = styled.p`
  font-family: ${fonts.inter};
  font-size: 0.9rem;
  opacity: 0.8;
  margin-bottom: 10px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: ${colors.white};
  opacity: 0.7;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    opacity: 1;
    color: ${colors.accent};
  }
`;

const TestimonialQuote = styled.blockquote`
  font-family: ${fonts.playfair};
  font-size: 1.2rem;
  font-style: italic;
  font-weight: 500;
  line-height: 1.6;
  margin-bottom: 0;
  position: relative;
  text-align: center;

  &::before {
    content: '"';
    font-size: 3rem;
    color: ${colors.accent};
    position: absolute;
    top: -10px;
    left: -15px;
    font-family: ${fonts.playfair};
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 30px;
`;

const CarouselDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.isActive ? colors.accent : 'rgba(255, 255, 255, 0.3)'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.isActive ? colors.accent : 'rgba(255, 255, 255, 0.5)'};
  }
`;

function TestimonialsSection() {
  const testimonials = [
    {
      quote: "No aprendí solo a hablar. Aprendí a confiar en mí.",
      author: "Raquel M.",
      role: "Facilitadora holística",
      image: "R",
      social: {
        linkedin: "https://linkedin.com/in/raquel-m",
        instagram: "@raquel.facilitadora"
      }
    },
    {
      quote: "Eduardo me ayudó a sanar emociones que ni sabía que me bloqueaban. Hoy hablo con fuerza y sin miedo.",
      author: "Laura G.",
      role: "Emprendedora",
      image: "L",
      social: {
        website: "www.laura-emprende.com",
        linkedin: "https://linkedin.com/in/laura-g"
      }
    },
    {
      quote: "He hecho muchos cursos. Este fue el único que cambió algo dentro de mí.",
      author: "Patricia R.",
      role: "Docente",
      image: "P",
      social: {
        instagram: "@patricia.educa",
        website: "patricia-educacion.com"
      }
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 7000); // Increased time to allow reading

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <TestimonialsContainer>
      <TestimonialsContent>
        <TestimonialsTitle>Ellas ya han pasado por este proceso</TestimonialsTitle>
        <TestimonialsSubtitle>
          Historias reales de transformación y liberación de la voz.
        </TestimonialsSubtitle>

        <TestimonialsCarousel>
          <NavigationButton direction="left" onClick={prevTestimonial}>
            ‹
          </NavigationButton>
          
          <NavigationButton direction="right" onClick={nextTestimonial}>
            ›
          </NavigationButton>

          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index} isActive={index === currentTestimonial}>
              <TestimonialHeader>
                <TestimonialImage>
                  {testimonial.image}
                </TestimonialImage>
                <TestimonialInfo>
                  <TestimonialAuthor>{testimonial.author}</TestimonialAuthor>
                  <TestimonialRole>{testimonial.role}</TestimonialRole>
                  <SocialLinks>
                    {testimonial.social.linkedin && (
                      <SocialLink href={testimonial.social.linkedin} target="_blank" rel="noopener noreferrer">
                        💼 LinkedIn
                      </SocialLink>
                    )}
                    {testimonial.social.instagram && (
                      <SocialLink href={`https://instagram.com/${testimonial.social.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer">
                        📷 {testimonial.social.instagram}
                      </SocialLink>
                    )}
                    {testimonial.social.website && (
                      <SocialLink href={`https://${testimonial.social.website}`} target="_blank" rel="noopener noreferrer">
                        🌐 {testimonial.social.website}
                      </SocialLink>
                    )}
                  </SocialLinks>
                </TestimonialInfo>
              </TestimonialHeader>
              
              <TestimonialQuote>
                {testimonial.quote}
              </TestimonialQuote>
            </TestimonialCard>
          ))}
        </TestimonialsCarousel>

        <CarouselDots>
          {testimonials.map((_, index) => (
            <CarouselDot
              key={index}
              isActive={index === currentTestimonial}
              onClick={() => setCurrentTestimonial(index)}
            />
          ))}
        </CarouselDots>
      </TestimonialsContent>
    </TestimonialsContainer>
  );
}

export default TestimonialsSection; 